// pfs-state.ts — dünner, platzierungs-agnostischer Zustands-Schalter für
// async-Content (loading / empty / error / loaded). KEIN Card-Wissen — du steckst
// ihn hin, WO die Zustandsgrenze liegen soll: IN einen pfs-card-body (Header bleibt
// dann konstant sichtbar), UM eine Card (ganze Card = loaded-State), in einen
// Header-Slot (kleiner inline-Spinner ↔ Badge), im Vollbild, in der Sidebar …
// Mehrere Regionen = mehrere <pfs-state> (z. B. je einer in Header UND Body, jeder
// mit eigenem Indikator/Text). So bleibt das WC schlank + komponierbar (kein Korsett).
//
// Komponierbar-mit-Defaults: jede Zustands-Region ist ein BENANNTER SLOT mit
// Default-Fallback. Slottest du nichts → bequemer Default aus data-*. Slottest du
// etwas → DEIN Inhalt gewinnt (der Fallback wird nativ verdeckt).
//
// Prinzip (Ports & Adapters): die App BESITZT den Fetch + setzt data-state; das WC
// BESITZT nur das Zeigen/Verstecken. State-Wechsel ist rein CSS (kein Re-Paint).
//
// Attribute (alle data-*):
//   data-state   loading (Default) | empty | error | loaded — treibt via CSS,
//                welche Region sichtbar ist.
//   data-loading-text                Text im Default-Spinner (Default "Wird geladen…").
//   data-empty-title/-message        Default-Leerzustand (<pfs-empty-state>).
//   data-error-title/-message        Default-Fehlerzustand.
//
// Slots (alle optional — jeder überschreibt seinen Default):
//   loading        ERSETZT den Default-<pfs-loading-indicator>.
//   empty          ERSETZT den Default-<pfs-empty-state>. (+ empty-icon / empty-action)
//   error          ERSETZT den Default-Fehlerzustand.     (+ error-action)
//   (default)      der GELADENE Inhalt (state=loaded). Alternativ: .content-Property.
//
// Property:
//   content        HTMLElement — imperativer Pfad für den geladenen Inhalt (statt
//                  Default-Slot, koexistiert — PFS-664). pre-connect-sicher.
//
// Beispiel (State IN den Body — Header bleibt sichtbar):
//   <pfs-card data-sectioned>
//     <pfs-card-header data-title="Praxis"><svg slot="icon"/></pfs-card-header>
//     <pfs-card-body>
//       <pfs-state data-state="loading" data-loading-text="Praxis wird geladen"
//                  data-empty-title="Keine Praxis" data-empty-message="Bitte wählen.">
//         <pfs-field-group>…geladener Inhalt…</pfs-field-group>
//       </pfs-state>
//     </pfs-card-body>
//   </pfs-card>
import { defineComponent } from "../../composition/define-component.js";
import { withShadow } from "../../composition/with-shadow.js";
import { takeOwnProperty } from "../../utils/upgrade-property.js";
import "../loading-indicator/pfs-loading-indicator.js";
import "../empty-state/pfs-empty-state.js";

const STYLES = `
:host { display: block; }
:host([hidden]) { display: none; }
/* Nur die aktive Region ist sichtbar (CSS-getrieben, kein Re-Paint). Ohne
   data-state = loading. */
.region { display: none; }
:host(:not([data-state])) .region-loading,
:host([data-state="loading"]) .region-loading,
:host([data-state="empty"])   .region-empty,
:host([data-state="error"])   .region-error,
:host([data-state="loaded"])  .region-loaded { display: block; }
/* Lädt/Leer/Fehler vertikal ZENTRIERT + reservieren via min-height denselben Raum
   → konsistent, egal ob Spinner (self-center) oder pfs-empty-state (top-padded).
   Der GELADENE Inhalt fließt normal (kein Zentrier-Zwang). Höhe als Naht. */
:host(:not([data-state])) .region-loading,
:host([data-state="loading"]) .region-loading,
:host([data-state="empty"])   .region-empty,
:host([data-state="error"])   .region-error {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: var(--pfs-state-min-height, 12rem);
}
`;

const TEMPLATE = `
<div class="region region-loading" part="loading">
  <slot name="loading"><pfs-loading-indicator data-variant="main"></pfs-loading-indicator></slot>
</div>
<div class="region region-empty" part="empty">
  <slot name="empty"><pfs-empty-state>
    <slot name="empty-icon" slot="icon"></slot>
    <slot name="empty-action" slot="action"></slot>
  </pfs-empty-state></slot>
</div>
<div class="region region-error" part="error">
  <slot name="error"><pfs-empty-state>
    <slot name="error-action" slot="action"></slot>
  </pfs-empty-state></slot>
</div>
<div class="region region-loaded" part="loaded">
  <slot></slot>
</div>`;

defineComponent({
  tagName: "pfs-state",
  mixins: [withShadow()],
  render(host, ctx) {
    if (!ctx.shadowRoot) return;
    const root = ctx.shadowRoot;
    const style = document.createElement("style");
    style.textContent = STYLES;
    root.appendChild(style);
    const mount = document.createElement("div");
    mount.innerHTML = TEMPLATE;
    root.appendChild(mount);

    // .content (imperativ) koexistiert mit dem Default-Slot. pre-connect-sicher.
    let content = takeOwnProperty<HTMLElement>(host, "content");
    Object.defineProperty(host, "content", {
      configurable: true,
      get: () => content,
      set: (v: HTMLElement | undefined) => {
        content = v;
        applyContent();
      },
    });
    const applyContent = (): void => {
      const loaded = mount.querySelector(".region-loaded");
      if (!loaded) return;
      loaded.querySelector('[data-content-host="1"]')?.remove();
      const slot = loaded.querySelector("slot") as HTMLElement | null;
      if (content) {
        const holder = document.createElement("div");
        holder.dataset.contentHost = "1";
        holder.appendChild(content);
        loaded.appendChild(holder);
        if (slot) slot.style.display = "none";
      } else if (slot) {
        slot.style.display = "";
      }
    };

    // Default-Fallback-Elemente aus data-* füttern (Overrides ignorieren das, da
    // der Fallback-Inhalt eines belegten Slots nativ nicht rendert). Reine
    // Attribut-Updates, kein Struktur-Rebuild.
    const li = mount.querySelector(".region-loading pfs-loading-indicator") as HTMLElement | null;
    const es = mount.querySelector(".region-empty pfs-empty-state") as HTMLElement | null;
    const errEl = mount.querySelector(".region-error pfs-empty-state") as HTMLElement | null;
    const paintDefaults = (): void => {
      if (li) li.setAttribute("data-text", host.dataset.loadingText || "Wird geladen…");
      if (es) {
        es.setAttribute("data-title", host.dataset.emptyTitle || "");
        es.setAttribute("data-message", host.dataset.emptyMessage || "");
      }
      if (errEl) {
        errEl.setAttribute("data-title", host.dataset.errorTitle || "Etwas ist schiefgelaufen");
        errEl.setAttribute("data-message", host.dataset.errorMessage || "");
      }
    };

    applyContent();
    paintDefaults();
    // data-state ist rein CSS → kein JS-Handler. Nur die Default-Texte reaktiv.
    const obs = new MutationObserver(paintDefaults);
    ctx.cleanup.push(() => obs.disconnect());
    obs.observe(host, {
      attributes: true,
      attributeFilter: [
        "data-loading-text", "data-empty-title", "data-empty-message",
        "data-error-title", "data-error-message",
      ],
    });
  },
});
